import type { ImageData, CDNImageData, ImageUnion } from '@lnr-v2/types';

/**
 * Gets the best image URL for display, supporting both:
 * - CDNImageData (new format: url from CDN, high quality)
 * - ImageData (legacy format: base64 full/thumbnail)
 */
export function getImageUrl(
  image: ImageUnion | undefined | null
): string | null {
  if (!image) return null;

  // New CDNImageData format (CDN URL)
  if ('url' in image && image.url) {
    return image.url;
  }

  // Legacy ImageData format (base64)
  if ('full' in image && image.full) {
    return image.full;
  }

  return null;
}

/**
 * Gets the preview/thumbnail for placeholder or fallback
 */
export function getImagePreview(
  image: ImageUnion | undefined | null
): string | null {
  if (!image) return null;

  // New CDNImageData format
  if ('preview' in image && image.preview) {
    return image.preview;
  }

  // Legacy ImageData format
  if ('thumbnail' in image && image.thumbnail) {
    return image.thumbnail;
  }

  // Fallback to main URL if no preview available
  return getImageUrl(image);
}

/**
 * Gets alt text from image data
 */
export function getImageAlt(image: ImageUnion | undefined | null): string {
  return image?.alt || '';
}

/**
 * Checks if image is using CDN format
 */
export function isCDNImage(
  image: ImageUnion | undefined | null
): image is CDNImageData {
  return image !== null && image !== undefined && 'url' in image;
}

/**
 * Checks if image is using legacy format
 */
export function isLegacyImage(
  image: ImageUnion | undefined | null
): image is ImageData {
  return image !== null && image !== undefined && 'full' in image;
}

// --- Supabase Image Transformations (WebP) ---

const SUPABASE_STORAGE_PATH = '/storage/v1/object/public/';
const SUPABASE_RENDER_PATH = '/storage/v1/render/image/public/';

interface TransformOptions {
  width?: number;
  quality?: number;
}

/**
 * Checks if a URL points to Supabase Storage
 */
export function isSupabaseUrl(url: string): boolean {
  return url.includes(SUPABASE_STORAGE_PATH);
}

/**
 * Transforms a Supabase Storage URL to use Image Transformations.
 * Replaces /object/public/ with /render/image/public/ and appends query params.
 * Non-Supabase URLs are returned unchanged.
 */
export function getTransformedUrl(
  url: string,
  options: TransformOptions = {}
): string {
  if (!isSupabaseUrl(url)) return url;

  const { width, quality = 80 } = options;

  const transformed = url.replace(SUPABASE_STORAGE_PATH, SUPABASE_RENDER_PATH);

  const params = new URLSearchParams();
  params.set('quality', String(quality));
  if (width) {
    params.set('width', String(width));
  }

  return `${transformed}?${params.toString()}`;
}

/**
 * Wrapper for raw URL strings (cover, thumbnailImage, etc.).
 * Returns null/undefined unchanged, transforms valid Supabase URLs.
 */
export function getTransformedImageUrl(
  url: string | undefined | null,
  options: TransformOptions = {}
): string | null {
  if (!url) return null;
  return getTransformedUrl(url, options);
}
